import merge from 'lodash/merge';
import mergeWith from 'lodash/mergeWith';
import pick from 'lodash/pick';
import {Platform} from 'react-native';
import I18n from 'react-native-i18n';
import fetch from '../api/fetch';
import config from '../config';
import availableTranslations from '../config/translations';
import theme from '../theme-config';
import AsyncStorage from '@react-native-async-storage/async-storage';
import WizzeeLog from './wizzeeLog';

if (Platform.OS === 'web') {
  const resolveBrowserLocale = () => {
    const {language, browserLanguage, userLanguage} = window.navigator;
    return language || browserLanguage || userLanguage || config.locales[0];
  };

  I18n.locale = resolveBrowserLocale();
}

I18n.fallbacks = true;

const allowedLocales = config.locales;
const initialTranslations = pick(availableTranslations, allowedLocales);

export const updateCustomTranslations = (shouldRefresh = true) => {
  const customTranslations = theme.translations;
  if (!customTranslations) {
    I18n.translations = initialTranslations;
  } else {
    I18n.translations = merge(initialTranslations, customTranslations);
  }

  if (shouldRefresh) {
    return refreshTranslation();
  }

  return Promise.resolve();
};

updateCustomTranslations(false);

const mergeTranslation = (translation, locale) => {
  if (translation) {
    WizzeeLog.log(
      '>>> [OK] static datas downloaded (%d bytes)',
      JSON.stringify(translation).length,
    );
  }
  I18n.translations[locale] = mergeWith(
    I18n.translations[locale],
    translation,
    (localTrad, distTrad, key) => {
      if (localTrad && typeof localTrad !== typeof distTrad) {
        WizzeeLog.warn(
          `Traduction conflict for key[${key}]: remote[${JSON.stringify(
            distTrad,
          )}] != local[${JSON.stringify(localTrad)}]`,
        );
        return localTrad;
      }
    },
  );
  return I18n.translations[locale];
};

const saveStaticDatasToLocalStorage = (staticDatas): Promise<any> => {
  AsyncStorage.setItem('STATIC_DATAS', JSON.stringify(staticDatas))
  .then(() => WizzeeLog.log('>>> [SAVED] static datas'))
  .catch(e =>
    WizzeeLog.error('Could not save static datas in local storage', e),
  );

  return Promise.resolve(staticDatas);
};

export const refreshTranslation = (): Promise<any> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 8000);

  return fetch(
    config.BASE_TRANSLATIONS_URL.replace('{language}', 'fr'),
    {
      method: 'GET',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        apikey: config.DIGICEL_STATIC_DATAS_APIKEY,
      },
    },
  )
    .finally(() => clearTimeout(timeout))
    .then(response => response.json())
    .then(staticDatas => saveStaticDatasToLocalStorage(staticDatas))
    .then((remoteTranslation: any) =>
      mergeTranslation(remoteTranslation, I18n.locale ?? 'fr'),
    )
    .catch(async e => {
      WizzeeLog.log('>>> [BACKUP] Cannot load static data online: \n', e);
      WizzeeLog.log('>>> [BACKUP] Trying to load static datas backup');
      const staticDatasBackup = await AsyncStorage.getItem('STATIC_DATAS');
      if (staticDatasBackup) {
        try {
          mergeTranslation(JSON.parse(staticDatasBackup), I18n.locale ?? 'fr');
          WizzeeLog.log('>>> [BACKUP] Loaded static datas backup');
        } catch (ex) {
          WizzeeLog.warn(
            'An error occured while merging translation.\nCannot refresh translation',
            ex,
          );
          return Promise.reject(ex);
        }
      } else {
        WizzeeLog.warn('Cannot refresh translation', e);
        return Promise.reject(e);
      }
    });
};

I18n.rtl = () => {
  if (!I18n.translations[I18n.locale]) {
    return false;
  }

  return I18n.translations[I18n.locale].rtl;
};

const getDefaultOptions = () => ({
  brand: config.brand,
  country: I18n.translate('countries.' + config.country),
});

I18n.t = (scope, options) => {
  if (I18n.locale === 'translationKeys') {
    return scope;
  }

  return I18n.translate(scope, {...getDefaultOptions(), ...options});
};

I18n.missingTranslation = function (key) {
  return 'NO TRANSLATION: ' + key;
};
I18n.has = key => !I18n.t(key).startsWith('NO TRANSLATION:');
I18n.getLocales = (withoutTranslationKeys: boolean = false) =>
  withoutTranslationKeys
    ? allowedLocales.filter(locale => locale !== 'translationKeys')
    : allowedLocales;
I18n.translateLocale = locale => availableTranslations[locale].locale;

const locale = I18n.locale.split('_')[0].split('-')[0];
I18n.locale =
  locale in I18n.translations || locale === 'translationKeys'
    ? locale
    : allowedLocales[0];

// https://github.com/AlexanderZaytsev/react-native-i18n/issues/79
I18n.toPrice = (number, options) => {
  return I18n.toCurrency(number, {
    unit: config.currency,
    format: I18n.t('prices.currency.format'),
    separator: I18n.t('prices.currency.separator'),
    delimiter: I18n.t('prices.currency.delimiter'),
    sign_first: I18n.t('prices.current.sign_first'),
    precision: Math.round(number) === number ? 0 : 2,
    ...options,
  });
};

export default I18n;
